import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export type OpenItemPriority = 'low' | 'medium' | 'high';

export interface BriefOpenItem {
  id: string;
  title: string;
  detail: string | null;
  priority: OpenItemPriority;
  opened_at: string;
  opened_by: string | null;
  opened_project_number: string | null;
}

export interface BriefRunStop {
  trip_id: string;
  project_number: string;
  stop: number | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  location_id: string;
  location_name: string | null;
  open_items: number;
}

export interface TripBrief {
  trip_id: string;
  location_id: string;
  generated_at: string;
  run: { run_id: string; run_number: string; title: string; this_stop: number | null; stops: BriefRunStop[] } | null;
  open_items: BriefOpenItem[];
  previous_visit: {
    trip_id: string;
    project_number: string;
    start_date: string | null;
    end_date: string | null;
    scope: string | null;
    imported: boolean;
    lead_technician: string | null;
    resolved_items: number;
    last_report: {
      report_date: string;
      work_completed: string | null;
      tomorrow_plan: string | null;
      punch_list: string | null;
      internal_notes: string | null;
    } | null;
  } | null;
  open_tickets: { id: string; ticket_number: string; title: string; priority: string | null; status: string; created_at: string }[];
  recent_leads: { id: string; observation: string; solution_type: string | null; priority: string | null; created_at: string }[];
  visit_count: number;
}

// trip_brief() and property_open_items are newer than database.types.ts.
type Untyped = {
  rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
  from: (t: string) => {
    insert: (row: Record<string, unknown>) => Promise<{ error: { message: string } | null }>;
    update: (patch: Record<string, unknown>) => { eq: (c: string, v: string) => Promise<{ error: { message: string } | null }> };
  };
};
const db = supabase as unknown as Untyped;

export const tripBriefKey = (tripId: string | undefined) => ['trip-brief', tripId] as const;

export function useTripBrief(tripId: string | undefined) {
  return useQuery({
    queryKey: tripBriefKey(tripId),
    enabled: Boolean(tripId),
    queryFn: async () => {
      const { data, error } = await db.rpc('trip_brief', { p_trip_id: tripId });
      if (error) throw new Error(error.message);
      return data as TripBrief;
    },
    // Keep the last good brief around: a crew often opens it in the car park
    // with patchy signal.
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function useAddOpenItem(tripId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { locationId: string; title: string; detail: string; priority: OpenItemPriority }) => {
      // customer_id is filled from the location by the database.
      const { error } = await db.from('property_open_items').insert({
        location_id: args.locationId,
        title: args.title,
        detail: args.detail || null,
        priority: args.priority,
        opened_trip_id: tripId ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tripBriefKey(tripId) }),
  });
}

export function useCloseOpenItem(tripId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: 'resolved' | 'wont_fix'; resolution: string }) => {
      const { error } = await db
        .from('property_open_items')
        .update({ status: args.status, resolution: args.resolution, resolved_trip_id: tripId ?? null })
        .eq('id', args.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tripBriefKey(tripId) }),
  });
}
